import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Loader2, Facebook, MapPin, Phone, Mail, ArrowUpRight } from "lucide-react";
import { PageHero, Reveal, PlaceholderTag } from "../components/common";
import { ORG } from "../data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inputCls =
  "w-full bg-transparent border-b border-charcoal/20 pb-3 pt-1 text-base focus:outline-none focus:border-charcoal transition-colors rounded-none placeholder:text-charcoal/35";
const SUBJECTS = ["General", "Volunteering", "Partnership", "Donation", "Media"];

export default function Contact() {
  const [params] = useSearchParams();
  const initial = SUBJECTS.includes(params.get("subject")) ? params.get("subject") : "General";
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: initial, message: "" });
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setStatus("submitting");
    try {
      await axios.post(`${API}/contact`, form);
      setStatus("success");
    } catch (err) {
      setStatus("idle");
      setError(err.response?.data?.detail || "Something went wrong. Please try again.");
    }
  };

  return (
    <>
      <PageHero overline="Contact" title="Get in Touch" copy="We welcome enquiries about our work, volunteering, partnerships and donations." />
      <section className="pb-24 md:pb-32">
        <div className="max-w-[90rem] mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-16">
          <div className="lg:col-span-5 space-y-10">
            <Reveal>
              <ul className="space-y-8">
                {[
                  { icon: MapPin, label: "Address", value: ORG.address },
                  { icon: Phone, label: "Phone", value: ORG.phone, href: `tel:${ORG.phone.replace(/\s/g, "")}` },
                  { icon: Mail, label: "Email", value: ORG.email, href: `mailto:${ORG.email}` },
                ].map(({ icon: Icon, label, value, href }) => (
                  <li key={label} className="flex gap-5">
                    <Icon className="h-5 w-5 text-terracotta shrink-0 mt-1" strokeWidth={1.75} />
                    <div>
                      <p className="text-xs font-bold uppercase tracking-[0.2em] text-charcoal/60">{label}</p>
                      {value ? (
                        href ? (
                          <a href={href} data-testid={`contact-${label.toLowerCase()}-link`} className="mt-2 inline-block text-base text-charcoal hover:text-terracotta transition-colors">
                            {value}
                          </a>
                        ) : (
                          <p className="mt-2 text-base text-charcoal">{value}</p>
                        )
                      ) : (
                        <div className="mt-2"><PlaceholderTag /></div>
                      )}
                    </div>
                  </li>
                ))}
                <li className="flex gap-5">
                  <Facebook className="h-5 w-5 text-terracotta shrink-0 mt-1" strokeWidth={1.75} />
                  <div>
                    <p className="text-xs font-bold uppercase tracking-[0.2em] text-charcoal/60">Social</p>
                    <a
                      href={ORG.facebook}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="contact-facebook-link"
                      className="mt-2 inline-flex items-center gap-1.5 text-base text-charcoal hover:text-terracotta transition-colors"
                    >
                      Facebook <ArrowUpRight className="h-4 w-4" />
                    </a>
                  </div>
                </li>
              </ul>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="rounded-2xl overflow-hidden border border-charcoal/10">
                <iframe
                  title="Map — Susanna Charitable Trust, Satellite, Ahmedabad"
                  src="https://www.google.com/maps?q=Siddhidarshan+Apartment,+Seema+Hall,+Satellite,+Ahmedabad+380015,+Gujarat,+India&output=embed"
                  className="w-full h-72 grayscale"
                  loading="lazy"
                  data-testid="contact-map"
                />
              </div>
              <p className="mt-3 text-xs text-charcoal/45">Siddhidarshan Apartment, Opp. Seema Hall, Satellite, Ahmedabad – 380015.</p>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            {status === "success" ? (
              <Reveal>
                <div data-testid="contact-success" className="bg-softgray rounded-2xl p-10 md:p-14 text-center">
                  <CheckCircle2 className="h-12 w-12 text-terracotta mx-auto" />
                  <h3 className="mt-6 font-serif text-3xl font-light tracking-tight">Message received.</h3>
                  <p className="mt-4 text-sm md:text-base text-charcoal/70 leading-relaxed">
                    Thank you, {form.name.split(" ")[0]}. Your enquiry has been sent to the Trust and we will
                    respond as soon as possible.
                  </p>
                </div>
              </Reveal>
            ) : (
              <Reveal>
                <form onSubmit={submit} data-testid="contact-form" className="space-y-10">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-10">
                    <div>
                      <label htmlFor="contact-name" className="text-xs font-bold uppercase tracking-[0.2em] text-charcoal/60">Name *</label>
                      <input id="contact-name" data-testid="contact-name-input" required value={form.name} onChange={set("name")} placeholder="Your name" className={inputCls} />
                    </div>
                    <div>
                      <label htmlFor="contact-email" className="text-xs font-bold uppercase tracking-[0.2em] text-charcoal/60">Email *</label>
                      <input id="contact-email" data-testid="contact-email-input" required type="email" value={form.email} onChange={set("email")} placeholder="you@example.com" className={inputCls} />
                    </div>
                    <div>
                      <label htmlFor="contact-phone" className="text-xs font-bold uppercase tracking-[0.2em] text-charcoal/60">Phone</label>
                      <input id="contact-phone" data-testid="contact-phone-input" type="tel" value={form.phone} onChange={set("phone")} placeholder="+91 ..." className={inputCls} />
                    </div>
                    <div>
                      <label htmlFor="contact-subject" className="text-xs font-bold uppercase tracking-[0.2em] text-charcoal/60">Subject</label>
                      <select id="contact-subject" data-testid="contact-subject-select" value={form.subject} onChange={set("subject")} className={`${inputCls} bg-warmwhite`}>
                        {SUBJECTS.map((s) => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </select>
                    </div>
                    <div className="sm:col-span-2">
                      <label htmlFor="contact-message" className="text-xs font-bold uppercase tracking-[0.2em] text-charcoal/60">Message *</label>
                      <textarea id="contact-message" data-testid="contact-message-input" required rows={4} value={form.message} onChange={set("message")} placeholder="How can we help?" className={`${inputCls} resize-none`} />
                    </div>
                  </div>
                  {error && <p data-testid="contact-error" className="text-sm font-medium text-terracotta-dark">{error}</p>}
                  <button
                    type="submit"
                    data-testid="contact-submit-button"
                    disabled={status === "submitting"}
                    className="rounded-full bg-charcoal text-warmwhite px-10 py-4 text-sm font-semibold hover:bg-terracotta hover:-translate-y-0.5 transition-all duration-300 disabled:opacity-60 disabled:translate-y-0 inline-flex items-center gap-2"
                  >
                    {status === "submitting" && <Loader2 className="h-4 w-4 animate-spin" />}
                    Send Message
                  </button>
                </form>
              </Reveal>
            )}
          </div>
        </div>
      </section>
    </>
  );
}
